/**
 * Cross-sell API surface (F7).
 *
 * Two calls in opposite directions. `getRecommendations` asks what else belongs on this
 * order; `logRecoEvents` reports back what was shown and what was taken, which is the
 * only source the accuracy number in the demo has. Without the second call the first one
 * is unfalsifiable.
 *
 * `logRecoEvents` never throws. It is telemetry attached to somebody else's task, and a
 * failed measurement must not surface as an error over an order screen that is working.
 */

import { API_PREFIX, apiGet, type Envelope } from '@/api/client'

/** How the suggestion was arrived at, in descending order of how much it knows. */
export type RecoStrategy = 'model' | 'customer' | 'popular' | 'upsell'

export interface Recommendation {
  product_id: string
  code: string
  name: string
  price: string
  unit_of_measure: string
  score: number
  confidence: number | null
  strategy: RecoStrategy
  /** Plain English, written by the server. Shown as-is; never re-worded here. */
  reason: string
  source_product_id: string | null
  source_product_name: string | null
  position: number
}

export interface Recommendations {
  items: Recommendation[]
  strategy: RecoStrategy
  model_version: string | null
  took_ms: number
  /**
   * Why this is not a model answer: `no_model`, `no_candidates`, `timeout`,
   * `model_error`, `customer_blocked`, `unknown_customer`. Present on a perfectly good
   * response -- it explains the answer, it does not report a failure to the user.
   */
  fallback_reason: string | null
  /**
   * M5's A/B arm: `control` (popularity only, the honest baseline) or `treatment` (model
   * first). Echoed straight back onto the impression/click events so the split holds up.
   */
  variant: string
}

export interface RecoQuery {
  customerId?: string | null
  productIds?: readonly string[]
  locationId?: string | null
  limit?: number
}

export interface RecoEvent {
  kind: 'impression' | 'click'
  strategy: RecoStrategy
  product_id: string
  source_product_id?: string | null
  customer_id?: string | null
  position?: number | null
  score?: number | null
  model_version?: string | null
  variant?: string | null
}

function query({ customerId, productIds = [], locationId, limit }: RecoQuery): string {
  const params = new URLSearchParams()
  if (customerId) params.set('customer_id', customerId)
  // Repeated rather than comma-joined: the endpoint takes a list, and a product code with
  // a comma in it would otherwise become two ids that match nothing.
  for (const id of productIds) params.append('product_id', id)
  if (locationId) params.set('location_id', locationId)
  if (limit) params.set('limit', String(limit))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const getRecommendations = (input: RecoQuery = {}) =>
  apiGet<Recommendations>(`${API_PREFIX}/recommendations${query(input)}`)

/** Step-up versions of what is on the order: same category and unit, a higher price. */
export const getUpsell = (productIds: readonly string[], limit = 4) =>
  apiGet<Recommendation[]>(`${API_PREFIX}/recommendations/upsell${query({ productIds, limit })}`)

export async function logRecoEvents(events: readonly RecoEvent[]): Promise<void> {
  if (events.length === 0) return
  try {
    await apiGet<Envelope<{ recorded: number }>>(`${API_PREFIX}/recommendations/events`, {
      method: 'POST',
      body: JSON.stringify({ events }),
    })
  } catch {
    // Swallowed on purpose. The rep is mid-order; a toast saying the analytics call
    // failed would be an interruption about something that is not their problem.
  }
}

/** Offline accuracy: train up to a cut-off, score the orders placed after it. */
export interface RecoEvaluation {
  baskets_evaluated: number
  k: number
  hit_rate_at_k: number
  precision_at_k: number
  recall_at_k: number
  coverage: number
  trained_on: number
  held_out_from: string
  held_out_to: string
}

export const evaluateRecommendations = () =>
  apiGet<RecoEvaluation>(`${API_PREFIX}/recommendations/evaluate`, { method: 'POST' })
